import {
  PropertyCondition,
  PropertyExternalMediaMatterportVirtualTourInput as MatterportExternalMedia,
  PropertyExternalMediaYoutubeVideoInput as YoutubeExternalMedia,
  PropertyOperation,
  PropertyType,
  type PropertyMediaInput,
  type CatalogProperty,
  type CatalogPropertyMediaItem,
  type CatalogPropertyCreateBodyParams,
  type CatalogPropertyUpdateBodyParams,
} from 'src/api/generated';
import type { PropertyFormValues } from '../validations/property-validations';
import { trimToUndefined } from 'src/modules/shared/utils/formatting/text.util';
import { isRecord } from 'src/modules/shared/utils/types.util';
import { propertyLandAccessInputSchema } from '../validations/property-land-access.schema';
import { getPropertyClassificationOption } from './property-classification.util';
import { propertyLocationFieldNames } from './property-location-field.util';

const propertyTypeByValue: Record<
  NonNullable<PropertyFormValues['propertyType']>,
  PropertyType
> = {
  HOUSE: PropertyType.HOUSE,
  APARTMENT: PropertyType.APARTMENT,
  OFFICE: PropertyType.OFFICE,
  RETAIL: PropertyType.RETAIL,
  WAREHOUSE: PropertyType.WAREHOUSE,
  LAND: PropertyType.LAND,
  PARCEL: PropertyType.PARCEL,
  PARKING: PropertyType.PARKING,
  INDUSTRIAL: PropertyType.INDUSTRIAL,
  OTHER: PropertyType.OTHER,
};

const propertyConditionByValue: Record<
  NonNullable<PropertyFormValues['condition']>,
  PropertyCondition
> = {
  USED: PropertyCondition.USED,
  NEW: PropertyCondition.NEW,
};

const propertyFeatureFieldNames = [
  'furnished',
  'petsAllowed',
  'hasVirtualTour',
  'hasBalcony',
  'hasKitchen',
  'hasLift',
  'hasPlayground',
  'hasRoofGarden',
  'hasBusinessCenter',
  'hasCinemaHall',
  'hasTennisCourt',
  'hasGarden',
  'hasPatio',
  'hasTerrace',
  'hasLaundry',
  'hasPool',
  'hasGym',
  'hasSecurity',
  'hasHeating',
  'hasAirConditioning',
  'hasInternetAccess',
] as const satisfies readonly (keyof PropertyFormValues)[];

const mapCatalogPropertyMediaItemToFormValue = (
  mediaItem: CatalogPropertyMediaItem
): PropertyFormValues['media'][number] | null => {
  const fileId = mediaItem.fileId.trim();

  if (!fileId) {
    return null;
  }

  return {
    fileId,
    kind: mediaItem.kind,
    position: mediaItem.position,
    sourceUrl: mediaItem.sourceUrl ?? undefined,
    sourceId: mediaItem.sourceId ?? undefined,
    sourceSystem: mediaItem.sourceSystem ?? undefined,
    title: mediaItem.title ?? undefined,
    altText: mediaItem.altText ?? undefined,
  };
};

/**
 * Builds the initial editor form state from an existing property or from the
 * default create values.
 */
export const getPropertyFormDefaults = (
  property?: CatalogProperty | null,
  defaultCurrency = ''
): PropertyFormValues => {
  const featureDefaults = propertyFeatureFieldNames.reduce<
    Partial<PropertyFormValues>
  >((result, fieldName) => {
    const value = readBooleanCustomField(property?.customFields, fieldName);
    if (value !== undefined) {
      result[fieldName] = value;
    }
    return result;
  }, {});

  return {
    userId: property?.userId ?? null,
    landAccess: propertyLandAccessInputSchema
      .nullable()
      .safeParse(
        property?.customFields &&
          ('landAccess' in property.customFields
            ? property.customFields.landAccess
            : property.customFields.string_landAccess)
      ).data,
    name: property?.name ?? '',
    description: property?.description ?? '',
    isActive: property?.isActive ?? true,
    operation: property?.operation ?? PropertyOperation.SALE_AND_RENT,
    salePriceAmount: property?.salePriceAmount ?? '',
    salePriceCurrency: property?.salePriceCurrency ?? defaultCurrency,
    rentPriceAmount: property?.rentPriceAmount ?? '',
    rentPriceCurrency: property?.rentPriceCurrency ?? defaultCurrency,
    sourceUrl: property?.sourceUrl ?? '',
    sourceSystem: property?.sourceSystem ?? '',
    sourceId: property?.sourceId ?? '',
    sourceCode: property?.sourceCode ?? '',
    mercadoLibreCode: property?.mercadoLibreCode ?? '',
    cadastralId: property?.cadastralId ?? '',
    countryCode: property?.countryCode ?? '',
    locality: property?.locality ?? '',
    region: property?.region ?? '',
    addressDisplay: property?.addressDisplay ?? '',
    streetName: property?.streetName ?? '',
    streetNumber: property?.streetNumber ?? '',
    unit: property?.unit ?? '',
    neighborhood: property?.neighborhood ?? '',
    externalMedia:
      property?.externalMedia?.map((item) =>
        item.provider === MatterportExternalMedia.provider.MATTERPORT
          ? {
              kind: MatterportExternalMedia.kind.VIRTUAL_TOUR,
              provider: MatterportExternalMedia.provider.MATTERPORT,
              url: item.url,
            }
          : {
              kind: YoutubeExternalMedia.kind.VIDEO,
              provider: YoutubeExternalMedia.provider.YOUTUBE,
              url: item.url,
            }
      ) ?? [],
    media:
      property?.media
        .map(mapCatalogPropertyMediaItemToFormValue)
        .filter(
          (mediaItem): mediaItem is PropertyFormValues['media'][number] =>
            mediaItem != null
        ) ?? [],
    latitude: property?.latitude ?? '',
    longitude: property?.longitude ?? '',
    propertyType: property?.propertyType ?? null,
    propertySubtype: property?.propertySubtype ?? null,
    condition: property?.condition ?? 'USED',
    bedrooms:
      typeof property?.bedrooms === 'number' ? String(property.bedrooms) : '',
    bathrooms:
      typeof property?.bathrooms === 'number' ? String(property.bathrooms) : '',
    rooms: typeof property?.rooms === 'number' ? String(property.rooms) : '',
    parkingLots:
      typeof property?.parkingLots === 'number'
        ? String(property.parkingLots)
        : '',
    warehouses:
      typeof property?.warehouses === 'number'
        ? String(property.warehouses)
        : '',
    unitFloor:
      typeof property?.unitFloor === 'number' ? String(property.unitFloor) : '',
    floors: typeof property?.floors === 'number' ? String(property.floors) : '',
    apartmentsPerFloor:
      typeof property?.apartmentsPerFloor === 'number'
        ? String(property.apartmentsPerFloor)
        : '',
    propertyAge:
      typeof property?.propertyAge === 'number'
        ? String(property.propertyAge)
        : '',
    orientation: property?.orientation ?? null,
    areaUsable: property?.areaUsable ?? '',
    areaBuilt: property?.areaBuilt ?? '',
    areaTotal: property?.areaTotal ?? '',
    areaOutdoor: property?.areaOutdoor ?? '',
    commonExpensesAmount: property?.commonExpensesAmount ?? '',
    ...featureDefaults,
  };
};

/**
 * Converts editor form values into the property mutation payload expected by
 * the catalog API.
 */
const buildPropertyMutationPayloadBase = (
  values: PropertyFormValues
): CatalogPropertyUpdateBodyParams => {
  const customFields = buildPropertyCustomFieldsPayload(values);

  return {
    ...(values.userId !== undefined ? { userId: values.userId } : {}),
    name: values.name.trim(),
    description: trimToUndefined(values.description),
    isActive: values.isActive,
    operation: values.operation,
    salePriceAmount: trimToUndefined(values.salePriceAmount),
    salePriceCurrency: trimToUndefined(values.salePriceCurrency),
    rentPriceAmount: trimToUndefined(values.rentPriceAmount),
    rentPriceCurrency: trimToUndefined(values.rentPriceCurrency),
    sourceUrl: trimToUndefined(values.sourceUrl),
    sourceSystem: trimToUndefined(values.sourceSystem),
    sourceId: trimToUndefined(values.sourceId),
    sourceCode: trimToUndefined(values.sourceCode),
    mercadoLibreCode: trimToUndefined(values.mercadoLibreCode),
    cadastralId: trimToUndefined(values.cadastralId),
    countryCode: normalizeLocationInput(values.countryCode) ?? undefined,
    locality: normalizeLocationInput(values.locality),
    region: normalizeLocationInput(values.region),
    addressDisplay: normalizeLocationInput(values.addressDisplay),
    streetName: normalizeLocationInput(values.streetName),
    streetNumber: normalizeLocationInput(values.streetNumber),
    unit: normalizeLocationInput(values.unit),
    neighborhood: normalizeLocationInput(values.neighborhood),
    externalMedia: values.externalMedia.map((item) =>
      item.provider === MatterportExternalMedia.provider.MATTERPORT
        ? {
            kind: MatterportExternalMedia.kind.VIRTUAL_TOUR,
            provider: MatterportExternalMedia.provider.MATTERPORT,
            url: item.url.trim(),
          }
        : {
            kind: YoutubeExternalMedia.kind.VIDEO,
            provider: YoutubeExternalMedia.provider.YOUTUBE,
            url: item.url.trim(),
          }
    ),
    media: values.media.map<PropertyMediaInput>((item, index) => ({
      fileId: item.fileId,
      kind: item.kind,
      position: item.position ?? index,
      sourceUrl: item.sourceUrl ?? undefined,
      sourceId: item.sourceId ?? undefined,
      sourceSystem: item.sourceSystem ?? undefined,
      title: item.title ?? undefined,
      altText: item.altText ?? undefined,
    })),
    latitude: normalizeLocationInput(values.latitude),
    longitude: normalizeLocationInput(values.longitude),
    propertyType: values.propertyType
      ? propertyTypeByValue[values.propertyType]
      : undefined,
    propertySubtype: getPropertyClassificationOption(
      values.propertyType,
      values.propertySubtype
    ).propertySubtype,
    condition: propertyConditionByValue[values.condition],
    bedrooms: parseOptionalNumber(values.bedrooms),
    bathrooms: parseOptionalNumber(values.bathrooms),
    rooms: parseOptionalNumber(values.rooms),
    parkingLots: parseOptionalNumber(values.parkingLots),
    warehouses: parseOptionalNumber(values.warehouses),
    unitFloor: parseOptionalNumber(values.unitFloor),
    floors: parseOptionalNumber(values.floors),
    apartmentsPerFloor: parseOptionalNumber(values.apartmentsPerFloor),
    propertyAge: parseOptionalNumber(values.propertyAge),
    orientation: values.orientation ?? undefined,
    areaUsable: trimToUndefined(values.areaUsable),
    areaBuilt: trimToUndefined(values.areaBuilt),
    areaTotal: trimToUndefined(values.areaTotal),
    areaOutdoor: trimToUndefined(values.areaOutdoor),
    commonExpensesAmount: trimToUndefined(values.commonExpensesAmount),
    commonExpensesCurrency: trimToUndefined(values.commonExpensesAmount)
      ? 'CLP'
      : undefined,
    customFields,
  };
};

/**
 * Converts editor values into the create payload shape.
 */
export const buildPropertyMutationPayload = (
  values: PropertyFormValues
): CatalogPropertyCreateBodyParams => {
  const base = buildPropertyMutationPayloadBase(values);

  return {
    ...base,
    name: values.name.trim(),
    condition: propertyConditionByValue[values.condition],
    isActive: values.isActive,
  };
};

/**
 * Converts editor values into the update payload shape without treating unchanged
 * blank geographic names as explicit clears of an existing code-only location.
 * @param values Current editor draft.
 * @param original Persisted property used to distinguish location changes.
 * @returns Update fields, including explicit clears only when location changes.
 */
export const buildPropertyUpdateMutationPayload = (
  values: PropertyFormValues,
  original?: CatalogProperty | null
): CatalogPropertyUpdateBodyParams => {
  const payload = buildPropertyMutationPayloadBase(values);
  const locationChanged =
    !original ||
    propertyLocationFieldNames.some(
      (field) =>
        field !== 'unit' &&
        normalizeLocationInput(values[field]) !==
          normalizeLocationInput(original[field])
    );
  if (locationChanged) return payload;

  const { region, locality, ...unchangedLocationPayload } = payload;
  return {
    ...unchangedLocationPayload,
    ...(region != null ? { region } : {}),
    ...(locality != null ? { locality } : {}),
  };
};

/**
 * Parses an optional numeric input from the property editor.
 */
export const parseOptionalNumber = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const normalized = trimmed.replace(',', '.');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : undefined;
};

function buildPropertyCustomFieldsPayload(
  values: PropertyFormValues
): Record<string, string | boolean | null> | undefined {
  const customFields = propertyFeatureFieldNames.reduce<
    Record<string, string | boolean | null>
  >((result, fieldName) => {
    const value = values[fieldName];
    if (typeof value === 'boolean' || value === null) {
      result[fieldName] = value;
    }
    return result;
  }, {});

  if (values.landAccess !== undefined) {
    customFields.landAccess = values.landAccess;
  }

  return Object.keys(customFields).length > 0 ? customFields : undefined;
}

function readBooleanCustomField(
  customFields: CatalogProperty['customFields'] | undefined,
  fieldName: (typeof propertyFeatureFieldNames)[number]
): boolean | undefined {
  if (!isRecord(customFields)) {
    return undefined;
  }

  const value = customFields[fieldName] ?? customFields[`bool_${fieldName}`];
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }
  }

  return undefined;
}

/** Converts empty location inputs to explicit clears in create/update payloads. */
function normalizeLocationInput(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed?.length ? trimmed : null;
}
